import {
  createDisabledWakeSignalDiagnostics,
  type WakeSignalDiagnosticsSnapshot,
} from "./wake-signal/wake-signal-diagnostics.js";
import {
  normalizeWakeSignalWorkerDiagnostics,
  type WakeSignalWorkersDiagnosticsSnapshot,
} from "./server-platform-worker-diagnostics.js";

type LoggerLike = Pick<Console, "error">;

type RouteContext = {
  req: {
    header(name: string): string | undefined;
  };
  json(body: unknown, status?: number): unknown;
};

type RouteRegistrar = {
  get(path: string, handler: (context: RouteContext) => Promise<unknown> | unknown): void;
};

const WAKE_SIGNAL_DIAGNOSTICS_INTERNAL_ERROR = "Internal wake signal diagnostics failed";

function isAuthorizedInternalToken(token: string | undefined, internalToken: string | undefined): boolean {
  if (!internalToken) return true;
  return token === internalToken;
}

export function createWakeSignalDiagnosticsRouteHandler(params: {
  getDiagnostics?: () => Promise<WakeSignalDiagnosticsSnapshot> | WakeSignalDiagnosticsSnapshot;
  getWorkerDiagnostics?: () => Promise<unknown> | unknown;
  internalToken?: string;
  logger?: LoggerLike;
}): (context: RouteContext) => Promise<unknown> {
  const logger = params.logger ?? console;
  return async (c) => {
    if (!isAuthorizedInternalToken(c.req.header("X-Internal-Token"), params.internalToken)) {
      return c.json({ error: "Forbidden" }, 403);
    }

    try {
      const diagnostics =
        (await params.getDiagnostics?.()) ?? createDisabledWakeSignalDiagnostics("disabled");
      const response: {
        ok: true;
        wakeSignal: WakeSignalDiagnosticsSnapshot;
        workers?: WakeSignalWorkersDiagnosticsSnapshot;
      } = {
        ok: true,
        wakeSignal: diagnostics,
      };
      if (params.getWorkerDiagnostics) {
        response.workers = normalizeWakeSignalWorkerDiagnostics(await params.getWorkerDiagnostics());
      }
      return c.json(response);
    } catch {
      logger.error("[wake-signal] diagnostics failed");
      return c.json({ error: WAKE_SIGNAL_DIAGNOSTICS_INTERNAL_ERROR }, 500);
    }
  };
}

export function registerWakeSignalDiagnosticsRoute(params: {
  app: RouteRegistrar;
  getDiagnostics?: () => Promise<WakeSignalDiagnosticsSnapshot> | WakeSignalDiagnosticsSnapshot;
  getWorkerDiagnostics?: () => Promise<unknown> | unknown;
  internalToken?: string;
  logger?: LoggerLike;
}): void {
  params.app.get(
    "/internal/wake-signal/diagnostics",
    createWakeSignalDiagnosticsRouteHandler({
      getDiagnostics: params.getDiagnostics,
      getWorkerDiagnostics: params.getWorkerDiagnostics,
      internalToken: params.internalToken,
      logger: params.logger,
    }),
  );
}
